import React, {
  useState, useRef, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import history from 'utils/history';
import PATIENT_RECORD from 'graph/queries/patientRecord';
import Dropdown, { Menu, MenuItem } from 'common/Dropdown';
import { logoutUser } from 'redux/auth/actions';
import MessageBadge from './MessageBadge';

const mapDispatchToProps = dispatch => (
  {
    onLogout: () => { dispatch(logoutUser()); },
  }
);

const MOBILE_MENU_ID = 'header-mobile-menu';

function HeaderMobile({ onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);
  const openWithKeyboardRef = useRef(false);

  const scheduleFocusFirstMenuItem = useCallback((menuId) => {
    const tryFocus = () => {
      const root = document.getElementById(menuId);
      if (!root) {
        requestAnimationFrame(tryFocus);
        return;
      }
      const first = root.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
      if (first && typeof first.focus === 'function') {
        first.focus();
      }
    };
    requestAnimationFrame(tryFocus);
  }, []);

  const handleVisibleChange = useCallback((visible) => {
    setMenuOpen(visible);
    if (!visible) {
      openWithKeyboardRef.current = false;
      return;
    }
    if (!openWithKeyboardRef.current) {
      return;
    }
    openWithKeyboardRef.current = false;
    scheduleFocusFirstMenuItem(MOBILE_MENU_ID);
  }, [scheduleFocusFirstMenuItem]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setMenuOpen(false);
        if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
          triggerRef.current.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [menuOpen]);

  const menu = (
    <Menu id={MOBILE_MENU_ID} onClick={handleClick} className="header-login-text">
      {renderTabs()}
    </Menu>
  );
  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      animation="slide-up"
      visible={menuOpen}
      onVisibleChange={handleVisibleChange}
    >
      <button
        type="button"
        id="header-profile"
        ref={triggerRef}
        className="flex items-center mr2 fw4 w-100"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={MOBILE_MENU_ID}
        aria-label="Main menu"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            openWithKeyboardRef.current = true;
            return;
          }
          if (!menuOpen && e.key === 'ArrowDown') {
            e.preventDefault();
            setMenuOpen(true);
            scheduleFocusFirstMenuItem(MOBILE_MENU_ID);
          }
        }}
      >
        <div id="header-login-text" className="pr3 pl3 pointer silver w-100 flex justify-end">
          <div className="header-tab flex items-center">
            <MessageBadge count={1} offset={[-1, -1]}>
              <i className="fas fa-bars ba pa2 f3 bw1 br2 pr3 pl3 pointer" aria-hidden />
            </MessageBadge>
          </div>
        </div>
      </button>
    </Dropdown>
  );

  function handleClick(info) {
    const { key } = info;
    switch (key) {
      case 'logout':
        onLogout();
        break;
      default:
        history.push(`/${key}`);
    }
  }

  function renderTabs() {
    const menu = [
      {
        key: 'my_tasks',
        iconClass: 'icon-clipboard-1 mr2',
        title: 'My Tasks',
      },
      {
        key: 'progress',
        iconClass: 'fas fa-chart-bar mr2',
        title: 'Progress',
      },
      {
        key: 'history',
        iconClass: 'fas fa-history mr2',
        title: 'History',
      },
      {
        key: 'messages',
        iconClass: 'fas fa-envelope mr2',
        title: (
          <MessageBadge offset={[5, 0]}>
            Messages
          </MessageBadge>
        ),
      },
      {
        key: 'settings',
        iconClass: 'fas fa-cog mr2',
        title: 'Settings',
      },
      {
        key: 'logout',
        iconClass: 'fas fa-power-off mr2',
        title: 'Log Out',
      },
    ];
    return menu.map(tabs => (
      <MenuItem key={tabs.key}>
        <div className="flex flex-row">
          <div className="w2">
            <i className={tabs.iconClass} />
          </div>
          {tabs.title}
        </div>
      </MenuItem>
    ));
  }
}

HeaderMobile.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

export default compose(
  connect(null, mapDispatchToProps),
  withRouter,
  graphql(PATIENT_RECORD, {
    props: ({ data: { patientRecord } }) => ({
      patientRecord,
    }),
  }),
)(HeaderMobile);
